<template>
  <vue-perfect-scrollbar
    ref="refChatLogPS"
    :settings="perfectScrollbarSettings"
    class="user-chats scroll-area"
    @ps-y-reach-start="handleReachTop"
    @ps-y-reach-end="handleReachBottom"
  >
    <div class="chat-log-container">
      <!-- Auto-refresh indicator -->
      <div v-if="isAutoRefreshing" class="auto-refresh-messages-indicator">
        <i class="fas fa-sync-alt fa-spin"></i>
        <span>{{ $t("pages.chat.updatingMessages") }}</span>
      </div>

      <!-- Initial loading state -->
      <div
        v-if="loadingChat && formattedChatData.formattedChatLog.length === 0"
        class="initial-loading"
      >
        <div class="loading-spinner">
          <div class="spinner"></div>
          <span class="loading-text">{{ $t("pages.chat.loadingConversation") }}</span>
        </div>
      </div>

      <div ref="chatContainer" class="chats">
        <div
          v-for="(msgGrp, index) in formattedChatData.formattedChatLog"
          :key="msgGrp.senderId + String(index)"
        >
          <div v-if="msgGrp.isDateSeparator" class="content-date-separator">
            <div
              class="date-separator"
              ref="dateSeparator"
              :data-label="msgGrp.dateLabel"
            >
              {{ msgGrp.dateLabel }}
            </div>
          </div>
          <!-- Bloque normal de chat -->
          <div
            v-else
            class="chat"
            :class="{
              'chat-right': msgGrp.senderId === 'assistant',
              'chat-left': msgGrp.senderId === 'user',
            }"
          >
            <div class="chat-body">
              <div
                v-for="msgData in msgGrp.messages"
                :key="msgData.idConversationMessage || msgData.time"
                class="chat-content"
              >
                <p
                  v-if="msgData.typeMessage === 'text'"
                  v-html="formatMessage(processMessage(msgData))"
                ></p>

                <div v-if="msgData.typeMessage === 'image'" class="file-content">
                  <img
                    :src="msgData.msg"
                    class="whatsapp-image-preview"
                    alt=""
                    @click="openImageModal(msgData.msg)"
                    loading="lazy"
                  />
                </div>
                <small>
                  {{ convertibleTime(msgData.time) }}
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Scroll to bottom button -->
      <div
        v-if="showScrollToBottomButton"
        class="scroll-to-bottom-btn"
        @click="forceScrollToBottom"
        :title="$t('pages.chat.scrollToBottom')"
      >
        <i class="fas fa-chevron-down"></i>
      </div>

      <!-- Modal para mostrar la imagen en tamaño completo -->
      <div
        v-if="isModalOpen"
        class="modal-overlay"
        @click="closeImageModal"
        role="dialog"
      >
        <transition name="fade">
          <div class="modal-content" @click.stop>
            <img
              :src="selectedImageUrl"
              :alt="$t('pages.chat.expandedImage')"
              class="modal-image"
            />
            <button
              class="close-button"
              @click="closeImageModal"
              :aria-label="$t('pages.chat.closeModal')"
            >
              {{ $t("pages.chat.close") }}
            </button>
          </div>
        </transition>
      </div>
    </div>
  </vue-perfect-scrollbar>
</template>

<script>
import { computed, ref, onUnmounted, nextTick } from "@vue/composition-api";
import { BAvatar } from "bootstrap-vue";
import moment from "moment";
import VuePerfectScrollbar from "vue-perfect-scrollbar";

export default {
  components: {
    BAvatar,
    // 3rd Party
    VuePerfectScrollbar,
  },
  props: {
    chatData: {
      type: Object,
      required: true,
    },
    profileUserAvatar: {
      type: String,
      required: true,
    },
    autoScroll: {
      type: Boolean,
      default: true,
    },
    isAutoRefreshing: {
      type: Boolean,
      default: false,
    },
    loadingChat: {
      type: Boolean,
      default: false,
    },
    currentPageMessage: {
      type: Number,
      required: true,
    },
    totalPagesMessage: {
      type: Number,
      required: true,
    },
    loadingMoreMessages: {
      type: Boolean,
      default: false,
    },
    allMessagesLoaded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isModalOpen: false,
      selectedImageUrl: null,
      isUserScrolling: false,
      scrollTimeout: null,
      lastScrollTop: 0,
      activeDateLabel: null,
      dateAnchors: [], // { el, top, label }
    };
  },
  setup(props, { root }) {
    const formattedChatData = computed(() => {
      const contact = {
        role: "assistant", // Identificamos al asistente
        avatar: "/img/assistant-avatar.png", // Avatar del asistente, puedes cambiarlo por otro
      };

      const refChatLogPS = ref(null);

      // let chatLog = props.chatData.chat.chat || [];
      let chatLog = [];
      if (props.chatData.chat) {
        chatLog = props.chatData.chat.chat;
      }
      if (!chatLog.length) return { formattedChatLog: [], contact };

      const formattedChatLog = [];
      let chatMessageSenderId = chatLog[0]?.senderId || undefined;

      let msgGroup = {
        senderId: chatMessageSenderId,
        messages: [],
      };

      let lastMessageDate = null;

      chatLog.forEach((msg, index) => {
        const msgDate = moment(msg.time).format("YYYY-MM-DD");
        // Insertar encabezado de fecha si cambia el día
        if (lastMessageDate !== msgDate) {
          formattedChatLog.push({
            isDateSeparator: true,
            dateLabel: formatDateLabel(msg.time),
          });
          lastMessageDate = msgDate;
        }

        if (chatMessageSenderId === msg.senderId) {
          msgGroup.messages.push({
            msg: msg.message,
            time: msg.time,
            typeMessage: msg.typeMessage,
            idConversationMessage: msg.idConversationMessage,
          });
        } else {
          chatMessageSenderId = msg.senderId;
          formattedChatLog.push(msgGroup);
          msgGroup = {
            senderId: msg.senderId,
            messages: [
              {
                msg: msg.message,
                time: msg.time,
                typeMessage: msg.typeMessage,
                idConversationMessage: msg.idConversationMessage,
              },
            ],
          };
        }
        if (index === chatLog.length - 1) formattedChatLog.push(msgGroup);
      });

      return {
        formattedChatLog,
        contact,
      };
    });

    // Función para mostrar "Hoy", "Ayer" o fecha normal
    function formatDateLabel(timestamp) {
      const locale = root.$i18n.locale || "es";
      const today = moment().locale(locale).startOf("day");
      const yesterday = moment().locale(locale).subtract(1, "day").startOf("day");
      const msgDate = moment(timestamp).locale(locale).startOf("day");

      if (msgDate.isSame(today)) return root.$t("pages.chat.today");
      if (msgDate.isSame(yesterday)) return root.$t("pages.chat.yesterday");
      return msgDate.format("DD MMM YYYY");
    }

    const perfectScrollbarSettings = {
      maxScrollbarLength: 150,
      suppressScrollX: true,
    };

    return {
      formattedChatData,
      perfectScrollbarSettings,
    };
  },

  watch: {
    "chatData.chat.chat": {
      handler(newChat, oldChat) {
        if (newChat && newChat.length > (oldChat?.length || 0)) {
          // Nuevo mensaje recibido
          this.$nextTick(() => {
            if (this.autoScroll && !this.isUserScrolling) {
              this.scrollToBottom();
            }
          });
        }
      },
      deep: true,
    },
  },

  computed: {
    showScrollToBottomButton() {
      const el = this.getScrollEl();
      if (!el) return false;
      const { scrollTop, scrollHeight, clientHeight } = el;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      return !isNearBottom && this.formattedChatData.formattedChatLog.length > 0;
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.scrollToBottomInstant();
      this.setupScrollListener();
      this.computeDateAnchors();
    });
  },

  updated() {
    this.$nextTick(() => {
      if (this.autoScroll && !this.isUserScrolling) this.scrollToBottom();
      this.computeDateAnchors();
    });
  },

  beforeDestroy() {
    this.removeScrollListener();
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
  },

  methods: {
    convertibleTime(data) {
      return moment(data).format("hh:mm A");
    },
    // Procesa el mensaje para detectar archivos y devolver texto sin URLs
    processMessage(msgData) {
      // Asegúrate de que msgData y msgData.msg sean válidos
      if (
        !msgData ||
        typeof msgData !== "object" ||
        !msgData.msg ||
        typeof msgData.msg !== "string"
      ) {
        return "";
      }
      const urlRegex = /(https?:\/\/[^\s\)]+)/g;

      const fileUrls = [];

      // Inicializa fileUrls en msgData si no existe
      if (!msgData.fileUrls) this.$set(msgData, "fileUrls", []);

      // Remueve las URLs del mensaje de texto y las guarda para mostrarlas debajo
      const processedMessage = msgData.msg.replace(urlRegex, (url) => {
        const fileType = this.getFileType(url);
        if (fileType !== "unknown") {
          fileUrls.push({ url, type: fileType });
          return "";
        }
        return `<a href="${url}" target="_blank">${url}</a>`;
      });

      // Agregar las URLs encontradas como propiedad del mensaje para renderizarlas aparte
      this.$set(msgData, "fileUrls", fileUrls);

      return processedMessage; // Devolver el mensaje procesado sin URLs de archivos
    },

    formatMessage(message) {
      return (
        message
          // Convertir **Texto** a <strong>Texto</strong>
          .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
          // Convertir saltos de línea a <br> para que se respeten en HTML
          .replace(/\n/g, "<br>")
      );
    },

    isFile(url) {
      // Expresión regular para verificar archivos comunes (incluye PDF y otros)
      const fileExtensions = /\.(pdf|doc|docx|xls|xlsx)(?=\?|$)/i;
      return fileExtensions.test(url);
    },

    isImage(url) {
      const imageExtensions = /\.(jpg|jpeg|png|gif)(?=\?|$)/i;
      return imageExtensions.test(url);
    },

    // Renderiza el ícono y el enlace de descarga para el archivo
    renderFileIcon(url) {
      const fileType = this.getFileType(url);
      switch (fileType) {
        case "pdf":
          return this.renderIconHTML(
            url,
            "fas fa-file-pdf",
            this.$t("pages.chat.pdfFile"),
          );
        // case 'image':
        //   return this.renderIconHTML(url, 'fas fa-image', 'Imagen Adjunta');
        case "word":
          return this.renderIconHTML(
            url,
            "fas fa-file-word",
            this.$t("pages.chat.wordDocument"),
          );
        case "excel":
          return this.renderIconHTML(
            url,
            "fas fa-file-excel",
            this.$t("pages.chat.excelSheet"),
          );
        default:
          return `<a href="${url}" target="_blank" class="download-link">${this.$t(
            "pages.chat.downloadFile",
          )}</a>`;
      }
    },

    // Función para detectar el tipo de archivo basado en expresión regular
    getFileType(url) {
      // const fileExtensionPattern = /\.(pdf|jpg|jpeg|png|gif|doc|docx|xls|xlsx)$/i;
      const fileExtensionPattern = url.match(
        /\.(jpg|jpeg|png|gif|pdf|doc|docx|xls|xlsx)(?=\?|$)/i
      );

      if (!fileExtensionPattern) return "unknown";

      const extension = fileExtensionPattern[1].toLowerCase();

      if (["jpg", "jpeg", "png", "gif"].includes(extension)) return "image";
      if (["pdf"].includes(extension)) return "pdf";
      if (["doc", "docx"].includes(extension)) return "word";
      if (["xls", "xlsx"].includes(extension)) return "excel";
      return "unknown";
    },

    // HTML para renderizar el ícono y el enlace de descarga del archivo
    renderIconHTML(url, iconClass, fileDescription) {
      return `
        <div class="chat-file">
          <span class="file-description">${fileDescription}</span>
       
          <a href="${url}" target="_blank" class="download-link">
            ${this.$t("pages.chat.download")}
          </a>
        </div>
      `;
    },

    // Función para descargar el archivo
    downloadFile(url) {
      const link = document.createElement("a");
      link.href = url;
      link.download = true;
      link.click();
    },

    // Abre la modal con la URL de la imagen seleccionada
    openImageModal(url) {
      this.selectedImageUrl = url;
      this.isModalOpen = true;
    },

    // Cierra la modal
    closeImageModal() {
      this.selectedImageUrl = null;
      this.isModalOpen = false;
    },

    // Función para obtener avatar según el ID del remitente
    getAvatar(senderId) {
      return senderId === "assistant"
        ? "/path/to/assistant-avatar.png"
        : "/path/to/user-avatar.png";
    },

    // Scroll management methods
    setupScrollListener() {
      const el = this.getScrollEl();
      if (el) el.addEventListener("scroll", this.handleScroll, { passive: true });
    },

    removeScrollListener() {
      const el = this.getScrollEl();
      if (el) el.removeEventListener("scroll", this.handleScroll);
    },

    handleScroll(event) {
      const container = event.target; // ahora es el PS real
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isScrollingUp = scrollTop < this.lastScrollTop;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

      if (isScrollingUp && !isNearBottom) {
        this.isUserScrolling = true;
        if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => (this.isUserScrolling = false), 2000);
      }
      if (isNearBottom) this.isUserScrolling = false;

      this.lastScrollTop = scrollTop;
      this.updateActiveDateOnScroll();
    },

    scrollToBottom(behavior = "auto") {
      const el = this.getScrollEl();
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    },

    scrollToBottomInstant() {
      const el = this.getScrollEl();
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    },

    forceScrollToBottom() {
      this.isUserScrolling = false;
      this.$nextTick(() => this.scrollToBottomInstant());
    },

    computeDateAnchors() {
      const container = this.getScrollEl();
      if (!container) return;

      const refs = this.$refs.dateSeparator || [];
      const els = Array.isArray(refs) ? refs : [refs];

      const cRect = container.getBoundingClientRect();
      this.dateAnchors = els
        .map((el) => {
          const r = el.getBoundingClientRect();
          const top = r.top - cRect.top + container.scrollTop; // relativo al scroll container
          const label = el.getAttribute("data-label") || "";
          return { el, top, label };
        })
        .sort((a, b) => a.top - b.top);

      this.updateActiveDateOnScroll();
    },

    updateActiveDateOnScroll() {
      const container = this.getScrollEl();
      if (!container || !this.dateAnchors.length) return;

      const scrollTop = container.scrollTop;
      const paddingTop = 16;
      let current = this.dateAnchors[0].label;
      for (const a of this.dateAnchors) {
        if (a.top <= scrollTop + paddingTop) current = a.label;
        else break;
      }
      this.activeDateLabel = current;
    },

    handleReachTop() {
      console.log("handleReachTop");
      // Evitar múltiples solicitudes simultáneas
      if (this.loadingMoreMessages || this.allMessagesLoaded) return;
      // Verificar si hay más páginas disponibles
      if (this.currentPageMessage >= this.totalPagesMessage) return;
      const nextPage = this.currentPageMessage + 1;
      this.loadNewerMessages(nextPage);
    },

    handleReachBottom() {
      console.log("handleReachBottom");
      if (this.loadingMoreMessages || this.allMessagesLoaded) return;
      const nextPage = 1;
      this.loadNewerMessages(nextPage);
    },

    async loadNewerMessages(page) {
      console.log("New message");
      const el = this.getScrollEl();
      if (!el) return;
      const scrollTopBefore = el.scrollTop;

      // ... aquí tu lógica de carga de mensajes nuevos ...
      this.$emit("load-newer-messages", page);

      this.$nextTick(() => {
        el.scrollTop = scrollTopBefore; // mantener posición relativa
      });
    },

    getScrollEl() {
      // elemento real que scrollea (raíz del VuePerfectScrollbar)
      return this.$refs.refChatLogPS && this.$refs.refChatLogPS.$el
        ? this.$refs.refChatLogPS.$el
        : null;
    },
  },
};
</script>

<style>
.chat-file {
  display: flex;
  align-items: center;
  /* background-color: #f6f6f6;
  border-radius: 8px;
  padding: 8px;
  margin: 10px 0; */
}

.file-icon {
  font-size: 20px;
  margin-right: 10px;
  color: #333;
}

.file-description {
  font-size: 14px;
  margin-right: 10px;
  font-weight: bold;
}

.download-link {
  background-color: #25d366;
  color: #000;
  padding: 5px 10px;
  border-radius: 5px;
  text-decoration: none;
  font-size: 14px;
  /* display: inline-flex; */
  align-items: center;
}

.download-link i {
  margin-right: 5px;
}

.download-link:hover {
  background-color: #1db954;
}

.whatsapp-image-preview {
  max-width: 200px;
  max-height: 200px;
  border-radius: 8px;
  margin-top: 5px;
  cursor: pointer;
  object-fit: cover;
}

.file-link,
.download-link {
  color: #000;
  text-decoration: none;
}

.chat-file {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Chat container and scroll styles */
.chat-log-container {
  position: relative;
  height: 100%;
  width: 100%;
}

.chats {
  /* height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  scroll-behavior: smooth; */
  padding: 20px;
  box-sizing: border-box;
}

/* Scrollbar customization */
/* .chats::-webkit-scrollbar {
  width: 6px;
}

.chats::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.chats::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.chats::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
} */

/* Scroll to bottom button */
.scroll-to-bottom-btn {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 50px;
  height: 50px;
  background: #7367f0;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(115, 103, 240, 0.4);
  transition: all 0.3s ease;
  z-index: 100;
}

.scroll-to-bottom-btn:hover {
  background: #5a4fdb;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(115, 103, 240, 0.5);
}

.scroll-to-bottom-btn i {
  font-size: 18px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .chats {
    padding: 15px;
  }

  .scroll-to-bottom-btn {
    bottom: 15px;
    right: 15px;
    width: 45px;
    height: 45px;
  }

  .scroll-to-bottom-btn i {
    font-size: 16px;
  }
}

@media (max-width: 480px) {
  .chats {
    padding: 10px;
  }

  .scroll-to-bottom-btn {
    bottom: 10px;
    right: 10px;
    width: 40px;
    height: 40px;
  }

  .scroll-to-bottom-btn i {
    font-size: 14px;
  }
}

/* Auto-refresh messages indicator */
.auto-refresh-messages-indicator {
  position: fixed;
  top: 20px;
  right: 20px;
  background: rgba(115, 103, 240, 0.9);
  color: white;
  padding: 8px 12px;
  border-radius: 20px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  z-index: 1000;
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(115, 103, 240, 0.3);
  animation: slideInRight 0.3s ease;
}

.auto-refresh-messages-indicator i {
  font-size: 10px;
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  position: relative;
  background: white;
  padding: 20px;
  border-radius: 8px;
  max-width: 90%;
  max-height: 90%;
  overflow: hidden;
}

.modal-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.close-button {
  position: absolute;
  top: 10px;
  right: 10px;
  background: #333;
  color: white;
  border: none;
  padding: 5px 10px;
  cursor: pointer;
  border-radius: 4px;
}

.date-separator {
  text-align: center;
  margin: 15px 0;
  padding: 5px 10px;
  background-color: #e1f3fb;
  color: #555;
  font-size: 12px;
  border-radius: 8px;
  display: inline-block;
}
.content-date-separator {
  width: 100%;
  justify-content: center;
  text-align: center;
}

/* Pastilla fija arriba (como WhatsApp) */
.date-sticky {
  position: sticky;
  top: 0;
  z-index: 5;
  display: inline-block;
  margin: 0 auto 8px;
  padding: 6px 10px;
  border-radius: 10px;
  background: #e1f3fb;
  color: #555;
  font-size: 12px;
  text-align: center;
}

.loading-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  margin: 10px 0;
}

.initial-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  margin: 20px 0;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 2px solid #e3e3e3;
  border-top: 2px solid #7367f0;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.spinner.small {
  width: 16px;
  height: 16px;
  border-width: 1.5px;
}

.loading-text {
  font-size: 12px;
  color: #6e6b7b;
  font-weight: 500;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
